import { ArrowRight, ExternalLink, Info, Trash } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { GetMollieConnectionsQuery as GetMollieConnections, MollieAppPricingType } from '__generated__/graphql';

import DeleteMollieConnectionMutation from './DeleteMollieConnectionMutation';
import MollieProfileSection from './MollieProfileSection';
import OptionsDropdown, { Option } from '../../common/Common/OptionsDropdown';
import UI from '../../common/UI';
import config from '../config';
import useMutation from '../../api/useMutation';
import useUrlState from '../../common/useUrlState';

type Project = GetMollieConnections['project'];
type MollieConnection = Project['mollie_connections'][0];

export interface MollieConnectionSectionProps {
  connection: MollieConnection;
  project: Project;
}

const MollieConnectionSection = ({ connection, project }: MollieConnectionSectionProps) => {
  const { t } = useTranslation();

  const [urlState] = useUrlState(({
    parse: (params, { string }) => ({
      ...params,
      connection_id: string(params.connection_id),
    }),
  }));

  const [deleteMollieConnection, { loading }] = (
    useMutation(DeleteMollieConnectionMutation, {
      variables: {
        id: connection.id,
      },
    })
  );

  const connected = connection.connected && connection.can_receive_payments;
  const noPayments = connection.connected && !connection.can_receive_payments;

  const hasManagedFees = connection.mollie_app.pricing_type === MollieAppPricingType.managed_fees;
  const organisationId = connection.organization_id;

  const justUpgradedToManagedFees = urlState.connection_id === connection.id
    && hasManagedFees
    && project.mollie_connections.filter(
      (connection) => connection.organization_id === organisationId
      && connection.mollie_app.pricing_type === MollieAppPricingType.client_pricing,
    ).length >= 1;

  return (
    <UI.Card sc={{ muted: loading }}>
      <UI.GridContainer>
        <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
          <UI.Div>
            <UI.H3>
              <UI.StatusIndicator
                sc={{ background: connected ? 'success' : (noPayments ? 'info' : 'error'), mt: -0.25, mb: 0.25 }}
              />
              {' '}
              {connection.organization_name || `(${t('no_organisation_name')})`}
            </UI.H3>
            <UI.Div sc={{ small: true }}>
              <UI.Delimit>
                <UI.Span sc={{ color: 'gray.400' }}>
                  {`#${connection.organization_id.replace('org_', '')}`}
                </UI.Span>
                <UI.A
                  href={`https://www.mollie.com/dashboard/${connection.organization_id}`}
                  target="_blank"
                  rel="noopener noreferer"
                >
                  {t('manage')}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              </UI.Delimit>
            </UI.Div>
          </UI.Div>

          {connection.deletable && (
            <OptionsDropdown>
              <Option
                icon={<Trash />}
                label={t('delete')}
                onClick={() => deleteMollieConnection()}
              />
            </OptionsDropdown>
          )}
        </UI.FlexContainer>

        {connection.connected && !hasManagedFees && !project.mollie_app && (
          <UI.Info icon={<Info />}>
            <UI.Strong>
              {t('old_mollie_pricing_title')}
            </UI.Strong>
            <UI.Paragraph>
              {t('old_mollie_pricing_description')}
            </UI.Paragraph>
            <UI.FlexContainer sc={{ spaceX: 1 }}>
              <UI.AButton
                href={`${config.backendPath}/api/mollie/authorize/${project.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sc={{ brand: 'secondary', size: 'sm' }}
              >
                {t('reconnect_with_mollie')}
                {' '}
                <UI.Icon>
                  <ArrowRight />
                </UI.Icon>
              </UI.AButton>
              <UI.AButton
                href="https://atleta.cc/article/payment-processing-fees"
                target="_blank"
                rel="noopener noreferrer"
                sc={{ brand: 'secondary', outline: true, size: 'sm' }}
              >
                {t('learn_more')}
                {' '}
                <UI.Icon>
                  <ExternalLink />
                </UI.Icon>
              </UI.AButton>
            </UI.FlexContainer>
          </UI.Info>
        )}

        {connection.connected && justUpgradedToManagedFees && (
          <UI.Success>
            <UI.Strong>
              {t('upgraded_to_managed_fees_title')}
            </UI.Strong>
            <UI.Paragraph sc={{ mb: 0 }}>
              {t('upgraded_to_managed_fees_description')}
            </UI.Paragraph>
          </UI.Success>
        )}

        {!connection.connected && (
          <UI.Warning>
            {t('mollie_disconnected')}
          </UI.Warning>
        )}

        {connection.connected && connection.profiles.length === 0 && (
          <UI.Info>
            {t('mollie_no_profiles')}
            {' '}
            <UI.A
              href={`https://www.mollie.com/dashboard/${connection.organization_id}/settings/profiles`}
              target="_blank"
              rel="noopener noreferer"
            >
              {t('manage_website_profiles')}
              {' '}
              <UI.Icon>
                <ExternalLink />
              </UI.Icon>
            </UI.A>
          </UI.Info>
        )}

        {connection.connected && connection.profiles.length > 0 && !connection.can_receive_payments && (
          <UI.Warning>
            {t('mollie_connected_but_payments_disabled')}
          </UI.Warning>
        )}

        {connection.connected && connection.profiles.length > 0 && (
          <UI.Card sc={{ hasPanels: true, outline: 'gray.200' }}>
            {connection.profiles.map((profile) => (
              <MollieProfileSection
                connection={connection}
                profile={profile}
                key={profile.id}
              />
            ))}
          </UI.Card>
        )}

        {connection.connected && connection.profiles.length > 0 && (
          <UI.Div>
            <UI.Span>
              {t('mollie_profiles_description')}
            </UI.Span>
            {' '}
            <UI.A
              href={`https://www.mollie.com/dashboard/${connection.organization_id}/settings/profiles`}
              target="_blank"
              rel="noopener noreferer"
            >
              {t('manage_website_profiles')}
              {' '}
              <UI.Icon>
                <ExternalLink />
              </UI.Icon>
            </UI.A>
          </UI.Div>
        )}
      </UI.GridContainer>
    </UI.Card>
  );
};

export default MollieConnectionSection;
